/**
 * HFACS (Human Factors Analysis and Classification System) Editor Component
 * AI-assisted human factors analysis using GPT-5.2
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Plus, X, Sparkles, Loader2, Brain, ChevronDown, ChevronUp 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// HFACS level colors
const HFACS_LEVEL_COLORS = {
  unsafe_acts: 'bg-red-500/20 border-red-500/50',
  preconditions: 'bg-amber-500/20 border-amber-500/50',
  unsafe_supervision: 'bg-orange-500/20 border-orange-500/50',
  organizational: 'bg-purple-500/20 border-purple-500/50'
};

export function HFACSEditor({ data, levels, onChange, token, onAISuggestApply }) {
  const factors = data?.factors || [];
  const [expandedLevel, setExpandedLevel] = useState('unsafe_acts');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  
  const addFactor = (level, category) => {
    const newFactor = {
      id: `factor_${Date.now()}`,
      level,
      category,
      description: '',
      is_contributing: true,
      severity: 'medium'
    };
    onChange({ ...data, factors: [...factors, newFactor] });
  };
  
  const updateFactor = (factorId, field, value) => {
    const updatedFactors = factors.map(f => 
      f.id === factorId ? { ...f, [field]: value } : f
    );
    onChange({ ...data, factors: updatedFactors });
  };
  
  const removeFactor = (factorId) => {
    onChange({ ...data, factors: factors.filter(f => f.id !== factorId) });
  };

  const requestAISuggestions = async () => {
    if (!data?.event_description) {
      toast.error('Please enter the event description first');
      return;
    }
    
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/investigations/ai/suggest-hfacs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          event_description: data.event_description,
          current_factors: factors.filter(f => f.description),
          incident_context: null
        })
      });
      
      if (res.ok) {
        const suggestions = await res.json();
        if (suggestions.error) {
          toast.error(suggestions.error);
        } else {
          setAiSuggestions(suggestions);
          toast.success('AI HFACS analysis generated');
        }
      } else {
        toast.error('Failed to get AI suggestions');
      }
    } catch (error) {
      toast.error('Error getting AI suggestions');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestions = () => {
    if (!aiSuggestions?.suggested_factors) return;
    
    const newFactors = aiSuggestions.suggested_factors.map(s => ({
      id: `factor_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      level: s.level,
      category: s.category,
      description: s.description,
      is_contributing: true,
      severity: s.severity || 'medium'
    }));
    
    onChange({
      ...data,
      factors: [...factors, ...newFactors],
      unsafe_acts_summary: aiSuggestions.unsafe_acts_summary,
      preconditions_summary: aiSuggestions.preconditions_summary,
      supervision_summary: aiSuggestions.supervision_summary,
      organizational_summary: aiSuggestions.organizational_summary
    });
    
    if (onAISuggestApply) {
      onAISuggestApply(aiSuggestions);
    }
    
    setAiSuggestions(null);
    toast.success('AI HFACS factors applied');
  };

  return (
    <div className="space-y-4">
      {/* AI Assistant Banner */}
      <Card className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 border-purple-500/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/30">
                <Brain className="w-5 h-5 text-purple-300" />
              </div>
              <div>
                <h4 className="font-medium text-white">AI HFACS Assistant</h4>
                <p className="text-sm text-slate-400">GPT-5.2 powered human factors analysis</p>
              </div>
            </div>
            <Button
              onClick={requestAISuggestions}
              disabled={aiLoading || !data?.event_description}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {aiLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Get AI Analysis</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AI Suggestions Panel */}
      {aiSuggestions && (
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-purple-300 text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> AI HFACS Analysis
                <Badge className="bg-purple-500/30 text-purple-300">{aiSuggestions.confidence}</Badge>
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAiSuggestions(null)}>Dismiss</Button>
                <Button size="sm" onClick={applyAISuggestions} className="bg-purple-600 hover:bg-purple-700">
                  Apply All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {aiSuggestions.suggested_factors?.slice(0, 6).map((f, idx) => (
              <div key={idx} className="p-2 rounded bg-slate-900/50 flex items-start gap-2">
                <Badge variant="outline" className="text-xs capitalize shrink-0">
                  {f.level?.replace('_', ' ')}
                </Badge>
                <div>
                  <p className="text-xs text-purple-300 capitalize">{f.category?.replace('_', ' ')}</p>
                  <p className="text-sm text-slate-300">{f.description}</p>
                </div>
              </div>
            ))}
            {aiSuggestions.primary_factors?.length > 0 && (
              <div className="p-2 rounded bg-red-500/10 border border-red-500/30">
                <p className="text-xs text-red-300 font-medium">Primary Factors:</p>
                <ul className="text-sm text-slate-300">
                  {aiSuggestions.primary_factors.map((pf, i) => <li key={i}>• {pf}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Event Description</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={data?.event_description || ''}
            onChange={(e) => onChange({ ...data, event_description: e.target.value })}
            placeholder="Describe the event being analyzed..."
            className="bg-slate-900/50 border-slate-600 min-h-[80px]"
          />
        </CardContent>
      </Card>
      
      <div className="space-y-3">
        {(levels || []).map(level => {
          const levelFactors = factors.filter(f => f.level === level.level);
          const isExpanded = expandedLevel === level.level;
          const levelColor = HFACS_LEVEL_COLORS[level.level] || 'bg-slate-500/20 border-slate-500/50';
          
          return (
            <Card key={level.level} className={`${levelColor} border`}>
              <CardHeader 
                className="pb-2 cursor-pointer"
                onClick={() => setExpandedLevel(isExpanded ? '' : level.level)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-white">{level.name}</CardTitle>
                    {levelFactors.length > 0 && (
                      <Badge variant="outline">{levelFactors.length} factors</Badge>
                    )}
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
                <p className="text-sm text-slate-400">{level.description}</p>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="space-y-4">
                  {/* Categories */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {(level.categories || []).map(cat => (
                      <Button
                        key={cat.key}
                        variant="outline"
                        size="sm"
                        className="justify-start text-xs"
                        onClick={() => addFactor(level.level, cat.key)}
                      >
                        <Plus className="w-3 h-3 mr-1" /> {cat.name}
                      </Button>
                    ))}
                  </div>
                  
                  {/* Added factors */}
                  {levelFactors.length > 0 && (
                    <div className="space-y-2 pt-2 border-t border-slate-700">
                      {levelFactors.map(factor => {
                        const category = level.categories?.find(c => c.key === factor.category);
                        return (
                          <div key={factor.id} className="p-3 rounded-lg bg-slate-900/50">
                            <div className="flex items-start justify-between mb-2">
                              <Badge variant="outline" className="text-xs">
                                {category?.name || factor.category}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFactor(factor.id)}
                              >
                                <X className="w-4 h-4 text-slate-400" />
                              </Button>
                            </div>
                            <Textarea
                              value={factor.description}
                              onChange={(e) => updateFactor(factor.id, 'description', e.target.value)}
                              placeholder="Describe the factor..."
                              className="bg-slate-800/50 border-slate-600 text-sm min-h-[60px]"
                            />
                            <div className="flex items-center gap-4 mt-2">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={factor.is_contributing}
                                  onCheckedChange={(checked) => updateFactor(factor.id, 'is_contributing', checked)}
                                />
                                <Label className="text-xs text-slate-400">Contributing Factor</Label>
                              </div>
                              <Select
                                value={factor.severity}
                                onValueChange={(v) => updateFactor(factor.id, 'severity', v)}
                              >
                                <SelectTrigger className="w-24 h-7 bg-slate-800/50 border-slate-600 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-slate-700">
                                  <SelectItem value="low">Low</SelectItem>
                                  <SelectItem value="medium">Medium</SelectItem>
                                  <SelectItem value="high">High</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
